@if (isPatientMode) {
  <ng-container *ngTemplateOutlet="profileContent"></ng-container>
} @else {
  <div class="patient-profile-page vc-view-shell">
    <main class="patient-profile-page__layout vc-view-content">
      <section class="patient-profile-page__frame vc-surface-panel">
        <ng-container *ngTemplateOutlet="profileContent"></ng-container>
      </section>
    </main>
  </div>
}

<ng-template #profileContent>
  @if (loading) {
    <p class="patient-profile__loading">{{ 'COMMON.LOADING' | translate }}</p>
  } @else {
    <section class="patient-profile__shell">
      <aside class="patient-profile__sidebar">
        <section class="patient-profile__summary">
          <div class="patient-profile__identity">
            <div class="patient-profile__avatar">
              @if (avatarUrl && !imageError) {
                <img [src]="avatarUrl" alt="avatar" (error)="onAvatarError()" />
              } @else {
                <span>{{ initials }}</span>
              }
            </div>
            <div class="patient-profile__identity-text">
              <h2>{{ form.name || '-' }} {{ form.surname || '' }}</h2>
              <p>{{ email || ('PATIENT_SPACE.PROFILE.NO_EMAIL' | translate) }}</p>
            </div>
          </div>
        </section>

        <nav class="patient-profile__nav">
          <button
            type="button"
            class="patient-profile__nav-item"
            [class.patient-profile__nav-item--active]="activeTab === 'personal'"
            (click)="activeTab = 'personal'"
          >
            <i class="fas fa-user"></i>
            <span>{{ 'PATIENT_SPACE.PROFILE.SECTIONS.PERSONAL' | translate }}</span>
          </button>
          <button
            type="button"
            class="patient-profile__nav-item"
            [class.patient-profile__nav-item--active]="activeTab === 'account'"
            (click)="activeTab = 'account'"
          >
            <i class="fas fa-id-card"></i>
            <span>{{ 'PATIENT_SPACE.PROFILE.SECTIONS.ACCOUNT' | translate }}</span>
          </button>
          <button
            type="button"
            class="patient-profile__nav-item"
            [class.patient-profile__nav-item--active]="activeTab === 'security'"
            (click)="activeTab = 'security'"
          >
            <i class="fas fa-lock"></i>
            <span>{{ 'COMMON.SECURITY' | translate }}</span>
          </button>
          @if (isPatientMode) {
            <button
              type="button"
              class="patient-profile__nav-item"
              [class.patient-profile__nav-item--active]="activeTab === 'privacy'"
              (click)="activeTab = 'privacy'"
            >
              <i class="fas fa-shield-halved"></i>
              <span>{{ 'PATIENT_SPACE.PROFILE.SECTIONS.PRIVACY' | translate }}</span>
            </button>
          }
        </nav>
      </aside>

      <section class="patient-profile__content">
        @if (requiresMinimumProfile) {
          <div class="patient-profile__notice">
            <strong>{{ 'PATIENT_SPACE.PROFILE.MINIMUM_NOTICE_TITLE' | translate }}</strong>
            <span>{{ 'PATIENT_SPACE.PROFILE.MINIMUM_NOTICE_TEXT' | translate }}</span>
          </div>
        }

        <article class="patient-profile__panel">
          @if (activeTab === 'personal') {
            <section class="patient-profile__section">
              <header class="vc-section-header vc-section-header--subsection">
                <div class="vc-section-header__main">
                  <h3 class="vc-section-header__title">{{ 'PATIENT_SPACE.PROFILE.SECTIONS.PERSONAL' | translate }}</h3>
                </div>
              </header>
              <div class="patient-profile__grid">
                <label>
                  {{ 'COMMON.NAME' | translate }}
                  <input type="text" [(ngModel)]="form.name" />
                </label>
                <label>
                  {{ 'COMMON.SURNAME' | translate }}
                  <input type="text" [(ngModel)]="form.surname" />
                </label>
                <label>
                  {{ 'PATIENT_SPACE.PROFILE.BIRTHDAY' | translate }}
                  <input type="date" [(ngModel)]="form.birthday" />
                </label>
                <label>
                  {{ 'PATIENT_SPACE.PROFILE.GENDER' | translate }}
                  <select [(ngModel)]="form.gender">
                    <option value="">{{ 'PATIENT_SPACE.PROFILE.GENDER_EMPTY' | translate }}</option>
                    <option value="MALE">{{ 'PATIENT_SPACE.PROFILE.GENDER_M' | translate }}</option>
                    <option value="FEMALE">{{ 'PATIENT_SPACE.PROFILE.GENDER_F' | translate }}</option>
                    <option value="OTHER">{{ 'PATIENT_SPACE.PROFILE.GENDER_O' | translate }}</option>
                  </select>
                </label>
                <label>
                  {{ 'COMMON.PHONE' | translate }}
                  <input type="text" [(ngModel)]="form.phone" />
                </label>
                <label>
                  {{ 'PATIENT_SPACE.PROFILE.ADDRESS' | translate }}
                  <input type="text" [(ngModel)]="form.address" />
                </label>
                <label>
                  {{ 'PATIENT_SPACE.PROFILE.POSTCODE' | translate }}
                  <input type="text" [(ngModel)]="form.postcode" />
                </label>
              </div>
            </section>
          }

      @if (activeTab === 'account') {
        <section class="patient-profile__section">
          <header class="vc-section-header vc-section-header--subsection">
            <div class="vc-section-header__main">
              <h3 class="vc-section-header__title">{{ 'PATIENT_SPACE.PROFILE.SECTIONS.ACCOUNT' | translate }}</h3>
            </div>
          </header>
          <div class="patient-profile__grid">
            <label>
              {{ 'COMMON.EMAIL' | translate }}
              <input type="email" [value]="email" readonly />
              <small>{{ 'PATIENT_SPACE.PROFILE.EMAIL_READONLY' | translate }}</small>
            </label>
            <label class="patient-profile__field-full patient-profile__upload">
              <span>{{ 'COMMON.IMAGE' | translate }}</span>
              <p-fileupload
                mode="basic"
                name="picture"
                [url]="uploadURL"
                [auto]="true"
                [withCredentials]="true"
                accept="image/*"
                [maxFileSize]="1000000"
                chooseStyleClass="p-button-primary p-button-sm"
                [chooseLabel]="'INDEX.PICTURE.UPDATE' | translate"
                (onUpload)="onPictureUploaded($event)"
                (onError)="onPictureUploadError($event)"
              ></p-fileupload>
              <small>{{ 'PATIENT_SPACE.PROFILE.IMAGE_HELP' | translate }}</small>
            </label>
          </div>
        </section>
      }

      @if (activeTab === 'security') {
        @if (hasLocalPassword) {
          <section class="patient-profile__section">
            <header class="vc-section-header vc-section-header--subsection">
              <div class="vc-section-header__main">
                <h3 class="vc-section-header__title">{{ 'COMMON.SECURITY' | translate }}</h3>
              </div>
            </header>
            <app-form-update-password
              [clearOnUpdate]="true"
              [payload]="{
                id: userId,
                password: undefined,
                password_confirmation: undefined
              }"
            ></app-form-update-password>
          </section>
        } @else {
          <section class="patient-profile__section">
            <header class="vc-section-header vc-section-header--subsection">
              <div class="vc-section-header__main">
                <h3 class="vc-section-header__title">{{ 'COMMON.SECURITY' | translate }}</h3>
              </div>
            </header>
            <p class="patient-profile__password-help">
              {{ 'PATIENT_SPACE.PROFILE.PASSWORD_GOOGLE_ONLY' | translate }}
            </p>
          </section>
        }
      }

      @if (isPatientMode && activeTab === 'privacy') {
        <section class="patient-profile__section">
          <header class="vc-section-header vc-section-header--subsection">
            <div class="vc-section-header__main">
              <h3 class="vc-section-header__title">{{ 'PATIENT_SPACE.PROFILE.SECTIONS.PRIVACY' | translate }}</h3>
              <p class="vc-section-header__subtitle">
                {{ 'PATIENT_SPACE.PROFILE.PRIVACY_HELP' | translate }}
              </p>
            </div>
          </header>
          <div class="patient-profile__privacy-grid">
            <label class="patient-profile__privacy-item">
              <input type="checkbox" [(ngModel)]="privacy.share_name" />
              <span>{{ 'COMMON.NAME' | translate }}</span>
            </label>
            <label class="patient-profile__privacy-item">
              <input type="checkbox" [(ngModel)]="privacy.share_surname" />
              <span>{{ 'COMMON.SURNAME' | translate }}</span>
            </label>
            <label class="patient-profile__privacy-item">
              <input type="checkbox" [(ngModel)]="privacy.share_email" />
              <span>{{ 'COMMON.EMAIL' | translate }}</span>
            </label>
            <label class="patient-profile__privacy-item">
              <input type="checkbox" [(ngModel)]="privacy.share_phone" />
              <span>{{ 'COMMON.PHONE' | translate }}</span>
            </label>
            <label class="patient-profile__privacy-item">
              <input type="checkbox" [(ngModel)]="privacy.share_birthday" />
              <span>{{ 'PATIENT_SPACE.PROFILE.BIRTHDAY' | translate }}</span>
            </label>
            <label class="patient-profile__privacy-item">
              <input type="checkbox" [(ngModel)]="privacy.share_gender" />
              <span>{{ 'PATIENT_SPACE.PROFILE.GENDER' | translate }}</span>
            </label>
            <label class="patient-profile__privacy-item">
              <input type="checkbox" [(ngModel)]="privacy.share_address" />
              <span>{{ 'PATIENT_SPACE.PROFILE.ADDRESS' | translate }}</span>
            </label>
            <label class="patient-profile__privacy-item">
              <input type="checkbox" [(ngModel)]="privacy.share_postcode" />
              <span>{{ 'PATIENT_SPACE.PROFILE.POSTCODE' | translate }}</span>
            </label>
          </div>
        </section>
      }

      @if (activeTab !== 'security') {
        <div class="patient-profile__actions">
          <button
            pButton
            type="button"
            icon="fas fa-save"
            class="p-button-primary p-button-sm"
            [label]="'COMMON.SAVE' | translate"
            [loading]="saving"
            [disabled]="saving"
            (click)="save()"
          ></button>
        </div>
      }
        </article>
      </section>
    </section>
  }
</ng-template>
